import React, { useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';

const Dashboard = () => {
  const [stats, setStats] = useState({
    career: 0,
    contact: 0
  });
  const [careerApplications, setCareerApplications] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch Career Applications
      const { data: caData, error: caError } = await supabase
        .from('career_applications')
        .select('*')
        .eq('status', 'new')
        .order('created_at', { ascending: false });

      if (caError && caError.code !== '42P01') console.error('Error fetching career applications:', caError);

      // Fetch Contact Messages
      const { data: cmData, error: cmError } = await supabase
        .from('contact_messages')
        .select('*')
        .eq('status', 'new')
        .order('created_at', { ascending: false });

      if (cmError && cmError.code !== '42P01') console.error('Error fetching contact messages:', cmError);

      setCareerApplications(caData || []);
      setContactMessages(cmData || []);

      setStats({
        career: (caData || []).length,
        contact: (cmData || []).length
      });

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (table, id) => {
    try {
      const { error } = await supabase
        .from(table)
        .update({ status: 'read' })
        .eq('id', id);

      if (error) throw error;

      // Refresh data
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  if (loading) return <div>Loading dashboard...</div>;

  return (
    <div>
      <h1 className="text-3xl font-bold mb-8 text-[#28357B]">Dashboard Overview</h1>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
        <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-[#FDA729]">
          <h3 className="text-gray-500 text-sm font-medium uppercase">New Career Applications</h3>
          <p className="text-3xl font-bold text-gray-800 mt-2">{stats.career}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-500">
          <h3 className="text-gray-500 text-sm font-medium uppercase">New Contact Messages</h3>
          <p className="text-3xl font-bold text-gray-800 mt-2">{stats.contact}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

        {/* Career Section */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="bg-orange-50 px-6 py-4 border-b border-orange-100 flex justify-between items-center">
            <h2 className="text-lg font-bold text-orange-800">Career Applications</h2>
          </div>
          <div className="divide-y divide-gray-100 max-h-[400px] overflow-y-auto">
            {careerApplications.length === 0 ? (
              <p className="p-6 text-gray-500 text-center">No new applications.</p>
            ) : (
              careerApplications.map((app) => (
                <div key={app.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h4 className="font-semibold text-gray-800">{app.name}</h4>
                      <p className="text-sm text-gray-600 font-medium">Applied for: {app.job_title}</p>
                      <div className="flex gap-3 mt-1">
                        <a href={`mailto:${app.email}`} className="text-xs text-blue-600 hover:underline">{app.email}</a>
                        <a href={`tel:${app.phone}`} className="text-xs text-blue-600 hover:underline">{app.phone}</a>
                      </div>
                    </div>
                    <span className="text-xs text-gray-400">{new Date(app.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="mt-3 flex gap-3">
                    <a
                      href={app.resume_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs bg-[#FDA729] text-white px-3 py-1 rounded hover:bg-orange-600"
                    >
                      View Resume
                    </a>
                    {app.linkedin_url && (
                      <a
                        href={app.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs bg-[#0077b5] text-white px-3 py-1 rounded hover:bg-[#006097]"
                      >
                        LinkedIn
                      </a>
                    )}
                    <button
                      onClick={() => markAsRead('career_applications', app.id)}
                      className="ml-auto text-xs text-gray-500 hover:text-gray-800"
                    >
                      Mark as Read
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Contact Messages Section */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="bg-blue-50 px-6 py-4 border-b border-blue-100 flex justify-between items-center">
            <h2 className="text-lg font-bold text-blue-800">Contact Messages</h2>
          </div>
          <div className="divide-y divide-gray-100 max-h-[400px] overflow-y-auto">
            {contactMessages.length === 0 ? (
              <p className="p-6 text-gray-500 text-center">No new messages.</p>
            ) : (
              contactMessages.map((msg) => (
                <div key={msg.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex flex-col md:flex-row justify-between items-start mb-2 gap-2">
                    <div>
                      <h4 className="font-semibold text-gray-800">{msg.name}</h4>
                      <p className="text-sm text-gray-500">{msg.email}</p>
                      {msg.company && <p className="text-xs text-gray-400">{msg.company}</p>}
                    </div>
                    <span className="text-xs text-gray-400 whitespace-nowrap">{new Date(msg.created_at).toLocaleDateString()} {new Date(msg.created_at).toLocaleTimeString()}</span>
                  </div>
                  <p className="text-gray-600 text-sm mb-4 mt-2 bg-gray-50 p-3 rounded">{msg.message}</p>
                  <button
                    onClick={() => markAsRead('contact_messages', msg.id)}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Mark as Read
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default Dashboard;
